/**
 * The game store.
 *
 * Shape of the thing: a transport emits validated `ServerMessage`s, this module
 * folds each one into `createMatchMachine` from `@geogame/rules` (invariant 6 —
 * the client predicts with the exact reducer the server authorises with), and
 * React reads the resulting `MatchState`. No game rule is reimplemented here.
 * What lives here is everything the reducer is deliberately not allowed to
 * own: decryption, the reveal schedule, the input gate, and the socket.
 *
 * INVARIANT 5 — the client never holds an answer key, so it cannot build a real
 * `MatchPlan`. It builds a *shell* plan instead (see `shellPlan`): the machine
 * only ever reads `plan.rounds.length`, and everything else it needs arrives
 * from the server as authority.
 *
 * INVARIANT 2 — every deadline in this file is in server time. `Date.now()`
 * appears exactly twice: to stamp `clientTs` on an outbound action (spec 8.3
 * wants the raw client clock, because the server subtracts the offset itself),
 * and inside `ClockSync`.
 */

import { create } from 'zustand';

import {
  HANDLE_MAX_LENGTH,
  TIER_CONFIG,
  indexAnswer,
  isRungInPlay,
  isTier,
  normalizeHandle,
  numericAnswer,
  pinpointAttemptPolicy,
  rungAssetIdx,
  windowCreditMs,
} from '@geogame/shared';
import type {
  AnswerReveal,
  ErrorCode,
  GameMode,
  LngLat,
  LobbyStateMessage,
  MatchKind,
  MatchEndMessage,
  MatchedMessage,
  NumericCreditBand,
  OppPhase,
  Pace,
  PinpointAttemptPolicy,
  PinpointAttemptState,
  RoundArmMessage,
  RoundEndMessage,
  RoundKeyMessage,
  RoundPayloadRung,
  RungResultMessage,
  ServerMessage,
  Tier,
} from '@geogame/shared';
import { createMatchMachine } from '@geogame/rules/machine';
import { breadcrumb, setFaultContext } from '../lib/telemetry';
import type { MatchMachine, MatchPlan, MatchState } from '@geogame/rules/types';

import { decryptJson, isSealedInline, openInlineJson } from '../lib/crypto';
import { RoundAssets } from '../lib/assets';
import { mergeRungs, parseRoundPayload, parseSealedRung } from '../lib/payload';
import { navigate } from '../lib/router';
import type { SignedIn } from '../lib/api';
import { todayKey } from '../lib/format';
import { SocketTransport } from '../net/socket';
import type { ConnectionStatus, GameTransport } from '../net/transport';
import {
  dailyKey,
  markPlayed,
  persistTier,
  persistedHandle,
  persistedTier,
  clearLocal,
  forgetAccount,
  helloToken,
  rememberAccount,
  rememberedAccount,
  writeLocal,
} from './storage';

/** How often the reducer is handed the clock. 80ms keeps rings smooth-adjacent
 *  while costing one shallow object comparison per tick. */
const TICK_MS = 80;

export type MatchMode = 'quick' | 'group' | 'daily' | 'friend' | 'practice';

/**
 * The tier the store boots on. Explorer for anybody this browser has never seen
 * play; otherwise the last tier they chose. `isTier` guards the stored string
 * because localStorage is user-writable and an unknown tier would index
 * TIER_CONFIG to undefined.
 */
function initialTier(): Tier {
  const stored = persistedTier();
  return stored !== null && isTier(stored) ? stored : 'explorer';
}

export interface RoundView {
  readonly roundIdx: number;
  /** Server instant of the reveal. The input gate compares against this. */
  readonly startAtServerTs: number;
  /** False until the local estimate of `startAtServerTs` has passed. */
  readonly revealed: boolean;
  /** Rung-1 prompt: the country name. */
  readonly prompt: string;
  readonly ladderDepth: number;
  readonly secondsPerRung: number;
  readonly decisionSeconds: number;
  /**
   * Extra seconds RUNG 1 runs for, because its prompt is a sentence. From the
   * arm frame, which is the only place the client can learn it: the shell plan
   * carries no prompts and the payload arrives too late to beat the tick.
   */
  readonly readSeconds: number;
  readonly orientRegion: string | null;
  /** Decrypted rung metadata, keyed by rung. Rung 1 is the pinpoint. */
  readonly rungs: Readonly<Record<number, RoundPayloadRung>>;
  /**
   * Object URLs for a picture rung's options, keyed by rung then option index.
   *
   * Populated only once that rung's key has been released — the bytes are
   * preloaded during the arm window (invariant 2: both players hold them before
   * the reveal) but they arrive sealed with the rung's own key, so they cannot
   * become pictures a moment earlier than the prompt does (invariant 5).
   */
  readonly assetUrls: Readonly<Record<number, readonly string[]>>;
  readonly lastResult: RungResultMessage | null;
  /** Rung whose answer is in flight; blocks a double submit locally. */
  readonly awaitingRung: number | null;
  /** Known only once the round resolves (invariant 5). */
  readonly targetCca3: string | null;
  readonly targetLngLat: LngLat | null;
  readonly targetLabel: string | null;

  // -- change 4: pinpoint retries ------------------------------------------
  /** What the tier allows and what each attempt is worth. From the payload. */
  readonly pinpointAttempts: PinpointAttemptPolicy;
  /**
   * The server's account of the attempts so far, or null before the first one.
   * This is what the retry meter reads; it is never derived locally, because
   * the count that matters is the one the server will charge against.
   */
  readonly attempts: PinpointAttemptState | null;

  // -- change 2: immediate feedback ----------------------------------------
  /**
   * The verdict on the player's OWN last pinpoint, the instant it comes back.
   *
   * INVARIANT 5. This names only the country the player themselves tapped —
   * `RungRetryMessage.pickedCca3`, echoed straight back — so it says nothing
   * about the target while the rung is still in play. When the rung RESOLVES,
   * the target arrives separately in `reveal`, and only then.
   */
  readonly pickFeedback: PickFeedback | null;

  // -- change 3: the reveal -------------------------------------------------
  /**
   * The correct answer, for a rung that is over. Structurally impossible to
   * hold for a rung still in play: `RungRetryMessage` declares `reveal?: never`
   * and `AnswerReveal` cannot be minted outside `sealReveal`.
   */
  readonly reveal: AnswerReveal | null;
  /** Which credit band a numeric submission landed in (change 6). */
  readonly numericBand: NumericCreditBand | null;

  // -- change 6: the golden round ------------------------------------------
  /**
   * This round is THE golden one. Arrives in the clear on `round.arm`, one
   * ROUND_ARM_LEAD_MS before the reveal, which is deliberate: the fire has to
   * be on screen BEFORE the prompt or it is not an event, it is a footnote.
   * It leaks nothing — which round is golden is a function of the match seed
   * and is identical for both seats.
   */
  readonly golden: boolean;
}

/**
 * The player's own pinpoint, graded. `seq` exists so a component can key an
 * animation off it: two wrong taps on the SAME country must shake twice.
 */
export interface PickFeedback {
  readonly seq: number;
  /** The country the player tapped. Null when they tapped ocean. */
  readonly cca3: string | null;
  readonly correct: boolean;
  /** True while the rung is still in play — a miss they can still recover. */
  readonly retryable: boolean;
}

export interface QueueView {
  readonly tier: Tier;
  readonly gameMode: GameMode;
  readonly kind: MatchKind;
  readonly startedAtServerTs: number;
  readonly estimatedWaitMs: number;
  /** Group queue only: seats the table fills to. */
  readonly groupTargetSeats?: number;
  /** Group queue only: when the server stops holding out for a group. */
  readonly groupFallbackAtServerTs?: number;
}

export interface GameState {
  /**
   * The link lobby this player is standing in, or null.
   *
   * Replaced wholesale on every `lobby.state`: the server re-sends the roster
   * on every join and leave, so the client never accumulates one and can never
   * be holding a stale member somebody else can already see has gone.
   */
  readonly lobby: LobbyStateMessage | null;
  /**
   * THIS player is under an eclipse for the current round: night globe, no
   * borders. Set from `round.arm`, which is per seat.
   */
  readonly eclipsed: boolean;
  /** The current round is the one the eclipse is being played for. */
  readonly eclipseRound: boolean;
  /**
   * The link this match came in on, kept so the results screen can offer a
   * rematch into the same lobby with the same people.
   */
  readonly inviteToken: string | null;
  readonly status: ConnectionStatus;
  readonly playerId: string | null;
  readonly handle: string;
  readonly rttMs: number;
  readonly offsetMs: number;
  readonly clockSynced: boolean;

  readonly mode: MatchMode | null;
  readonly gameMode: GameMode;
  readonly tier: Tier;
  readonly courseId: string | null;
  readonly queue: QueueView | null;
  readonly matchInfo: MatchedMessage | null;
  readonly match: MatchState | null;
  readonly round: RoundView | null;

  /** Opponent's live phase, for the ladder animation. */
  readonly oppPhase: OppPhase;
  /** Bumped on every opponent transition so components can key an animation. */
  readonly oppSeq: number;

  /**
   * Which round index is golden, known from `matched` — i.e. from the moment
   * the match exists, so the ladder can flag it rounds in advance. Null when
   * the server did not say (an older gateway, or a synthetic match).
   */
  readonly goldenRoundIdx: number | null;

  /**
   * This match is the GUIDED first round: an offline practice match with a
   * coaching line at each beat. Purely presentational — it changes nothing
   * about scoring, timing or the machine, only what the HUD says alongside it.
   */
  readonly coached: boolean;

  readonly history: readonly RoundEndMessage[];
  readonly matchEnd: MatchEndMessage | null;
  readonly lastError: { readonly code: ErrorCode; readonly message: string } | null;

  // -- actions
  readonly startMatch: (opts: {
    mode: MatchMode;
    gameMode?: GameMode;
    tier: Tier;
    courseId?: string;
    shareToken?: string;
    seed?: string;
    /** Render the guided coaching lines over this match. See `coached`. */
    coached?: boolean;
    /**
     * How long each round gets, for an OFFLINE match only.
     *
     * A live match takes its pace from the lobby, and the room sends it back
     * on `matched` -- so passing it here would be this browser telling itself
     * something the server has the final word on.
     */
    pace?: Pace;
    /**
     * Substitute transport. The seam exists so the frame handling in this file
     * can be driven by a harness that emits exactly what the gateway emits —
     * inline-sealed deeper rungs, a resync burst — without a socket. Nothing in
     * the app passes it.
     */
    transport?: GameTransport;
  }) => void;
  /** Start the match with whoever is in the lobby. Any member may call it. */
  readonly startLobby: () => void;
  /**
   * Change the name this player is sitting under, on a socket that is already
   * open, and tell the table. Local-only when there is no socket.
   */
  readonly renameInLobby: (handle: string) => void;
  /** Change what the lobby will play. The HOST only; the gateway ignores others. */
  readonly configureLobby: (next: {
    tier?: Tier;
    gameMode?: GameMode;
    courseId?: string | null;
    pace?: Pace;
  }) => void;
  /**
   * Hand the settings to another member.
   *
   * For the host who has stopped wanting to be asked what everybody is
   * playing, which on an evening that runs long is everybody eventually.
   */
  readonly promoteInLobby: (playerId: string) => void;
  /**
   * Hand this browser back to nobody.
   *
   * It drops the IDENTITY as well as the remembered address, and it has to.
   * Keeping the token would leave the next person at this computer holding the
   * player id of the last one -- their coverage, their rivalries, their name --
   * which is the precise thing somebody signing out on a shared machine is
   * trying to prevent. `welcome` mints a fresh anonymous identity on the next
   * connection, exactly as it does for a browser that has never played.
   */
  readonly signOut: () => void;
  readonly cancelQueue: () => void;
  readonly leaveMatch: () => void;
  readonly submitPinpoint: (lngLat: LngLat, cca3: string | null) => boolean;
  readonly submitAnswer: (optionIdx: number) => boolean;
  /** The open numeric answer of a golden round's terminal rung (change 6). */
  readonly submitNumeric: (value: number) => boolean;
  readonly bank: () => boolean;
  readonly push: () => boolean;
  readonly serverNow: () => number;
  readonly setHandle: (handle: string) => void;
  /**
   * The account this browser is signed into, or null.
   *
   * Only ever set by a completed sign-in. There is no "am I signed in?" call
   * on boot: the identity token in localStorage is what makes a device this
   * player, signed in or not, and asking the server to confirm an account it
   * has no session for would be a round trip that changes nothing.
   */
  readonly account: { readonly email: string } | null;
  /**
   * Adopt whoever this device is after signing in.
   *
   * The id CAN CHANGE HERE. Signing in on a device that had been playing
   * anonymously adopts the account's player and folds this device's history
   * into it, so the token, the id and the name are all replaced rather than
   * merged in place.
   */
  readonly adoptIdentity: (next: SignedIn) => void;
  readonly dismissError: () => void;
}

// ---------------------------------------------------------------------------
// Module-local, deliberately outside React state: these are identities, not
// values, and re-rendering on them would be meaningless.
// ---------------------------------------------------------------------------

let transport: GameTransport | null = null;
/**
 * Monotonic id of the most recent `startMatch`, bumped by `teardown` too.
 *
 * The practice driver arrives through a dynamic import (invariant 5 — see the
 * comment at its call site), so there is a real window in which the store can
 * be torn down or restarted while the chunk is still in flight. Attaching a
 * driver into a store that has moved on is how a stale transport ends up
 * feeding frames into somebody's next match.
 */
let startToken = 0;
let machine: MatchMachine | null = null;
let unsubscribe: Array<() => void> = [];
let tickTimer: number | null = null;
let revealTimer: number | null = null;
/**
 * The post-match hand-off to the results screen. Held here because it MUST be
 * cancellable: a player who taps "Play again" inside 1.4s starts a new match,
 * and a stale timer would then navigate them off the queue screen and into the
 * previous match's results.
 */
let matchEndTimer: number | null = null;
let pendingArms: Array<{
  roundIdx: number;
  iv: string;
  ciphertext: string;
  startAtServerTs: number;
  /** True when this arm carries a deeper rung for a round already in progress. */
  delivery: boolean;
  /** Set once its envelope has been opened; a later key is for a deeper rung. */
  opened: boolean;
}> = [];
/** Deeper-rung keys whose sealed blob has not been decrypted into view yet. */
let pendingRungKeys: Array<{ roundIdx: number; rung: number | null; key: string }> = [];
/**
 * The live round's artwork: sealed bytes pulled during the arm window, opened
 * when the rung that shows them is released. Module-local for the same reason
 * the timers are — it is an identity, not a value, and nothing re-renders on it.
 */
let roundAssets = new RoundAssets();
/**
 * Monotonic tag on pinpoint feedback. Module-local for the same reason the
 * timers are: it is an identity for keying an animation, not a value anything
 * should re-render on by itself.
 */
let feedbackSeq = 0;
/** Tokens the CURRENT arm shipped, in the order `assetIdx` indexes them. */
let armAssetTokens: { roundIdx: number; tokens: readonly string[] } = {
  roundIdx: -1,
  tokens: [],
};

/**
 * Rebuild an attempt picture from a bare count, for the one frame that carries
 * a count rather than a state: the resume snapshot.
 *
 * Deliberately reports `nextMaxPoints: 0`. What the next attempt would pay is a
 * function of the latency it lands at, and after a reconnect this client has no
 * honest idea how long the rung has been open. A wrong number on the retry
 * meter is worse than none, so the UI shows the multiplier and omits the
 * points until the server's own state arrives with the next result.
 */
function attemptStateFor(round: RoundView, attempt: number): PinpointAttemptState {
  const policy = round.pinpointAttempts;
  const remaining = Math.max(0, policy.maxAttempts - attempt);
  return {
    attempt,
    maxAttempts: policy.maxAttempts,
    attemptsRemaining: remaining,
    nextMultiplier: remaining > 0 ? (policy.multipliers[attempt] ?? 0) : 0,
    nextMaxPoints: 0,
  };
}

/**
 * The machine reads exactly one field of the plan — `rounds.length` — so the
 * client can satisfy it without ever seeing a country code or an answer key.
 * Handing the client a real plan would hand it the match (invariant 5).
 */
function shellPlan(tier: Tier, roundCount: number, gameMode: GameMode): MatchPlan {
  return {
    seed: '',
    tier,
    gameMode,
    courseId: null,
    rounds: Array.from({ length: Math.max(1, roundCount) }, (_, index) => ({
      index,
      cca3: '',
      rungs: [],
    })),
  };
}

export const useGame = create<GameState>()((set, get) => {
  // -- plumbing -------------------------------------------------------------

  const syncMachine = (): void => {
    if (!machine) return;
    const next = machine.getState();
    if (next !== get().match) set({ match: next });
  };

  const dispatch = (event: Parameters<MatchMachine['dispatch']>[0]): void => {
    if (!machine) return;
    machine.dispatch(event);
    syncMachine();
  };

  const stopTicker = (): void => {
    if (tickTimer !== null) window.clearInterval(tickTimer);
    tickTimer = null;
  };

  const startTicker = (): void => {
    stopTicker();
    tickTimer = window.setInterval(() => {
      if (!machine || !transport) return;
      dispatch({ t: 'local.tick', serverTs: transport.serverNow() });
    }, TICK_MS);
  };

  const teardown = (): void => {
    // Invalidates any practice driver still being imported. See `startToken`.
    startToken += 1;
    stopTicker();
    if (revealTimer !== null) window.clearTimeout(revealTimer);
    revealTimer = null;
    if (matchEndTimer !== null) window.clearTimeout(matchEndTimer);
    matchEndTimer = null;
    for (const off of unsubscribe) off();
    unsubscribe = [];
    pendingArms = [];
    pendingRungKeys = [];
    // Object URLs are a leak if nothing revokes them, and the bytes behind them
    // are one round's worth of artwork.
    roundAssets.release();
    roundAssets = new RoundAssets();
    armAssetTokens = { roundIdx: -1, tokens: [] };
    transport?.close();
    transport = null;
    machine = null;
  };

  // -- armed round handling (spec 8.2) --------------------------------------

  const onArm = (msg: RoundArmMessage): void => {
    const current = get().round;
    // A deeper rung's prompt names the country, so it cannot ship before rung 1
    // is locked. The gateway therefore re-arms the same round once the player
    // pushes; that is a payload delivery, not a new round.
    const delivery = current !== null && current.roundIdx === msg.roundIdx && current.revealed;

    // Arms for an older round can never be opened again — the deeper-rung keys
    // that follow always belong to the live round.
    pendingArms = pendingArms.filter((a) => a.roundIdx === msg.roundIdx);
    pendingRungKeys = pendingRungKeys.filter((k) => k.roundIdx === msg.roundIdx);
    pendingArms.push({
      roundIdx: msg.roundIdx,
      iv: msg.iv,
      ciphertext: msg.payloadCiphertext,
      startAtServerTs: msg.startAtServerTs,
      delivery,
      opened: false,
    });

    rememberArmAssets(msg.roundIdx, msg.assetTokens);

    // A DELIVERY re-arm is a push's payload for a round that is already open:
    // the target has been public since T+0, so there is nothing left to leak
    // and the bytes are wanted now.
    if (delivery) {
      startAssetPreload(msg.roundIdx);
      return;
    }

    dispatch(msg);
    const cfg = TIER_CONFIG[get().tier];
    set({
      round: {
        roundIdx: msg.roundIdx,
        startAtServerTs: msg.startAtServerTs,
        revealed: false,
        prompt: '',
        ladderDepth: cfg.ladderDepth,
        secondsPerRung: cfg.secondsPerRung,
        decisionSeconds: cfg.decisionSeconds,
        readSeconds: msg.readSeconds ?? 0,
        orientRegion: null,
        rungs: {},
        assetUrls: {},
        lastResult: null,
        awaitingRung: null,
        targetCca3: null,
        targetLngLat: null,
        targetLabel: null,
        // The tier's own policy until the payload lands with the server's copy.
        // They agree — both read TIER_CONFIG — but the payload is authority.
        pinpointAttempts: pinpointAttemptPolicy(get().tier),
        attempts: null,
        pickFeedback: null,
        reveal: null,
        numericBand: null,
        golden: msg.golden === true || get().goldenRoundIdx === msg.roundIdx,
      },
      oppPhase: 'arming',
    });
  };

  /**
   * Remember the round's asset tokens. Does NOT fetch — see `startAssetPreload`.
   */
  const rememberArmAssets = (roundIdx: number, tokens: readonly string[] | undefined): void => {
    if (roundIdx !== armAssetTokens.roundIdx) {
      // A new round's artwork replaces the last one's; the URLs behind the old
      // bytes are revoked here rather than at some later garbage-collection.
      roundAssets.release();
    }
    armAssetTokens = { roundIdx, tokens: tokens ?? [] };
  };

  /**
   * INVARIANT 2, the asset half. The pixels are pre-fetched so that at the
   * moment a picture rung opens both players already hold the bytes, rather
   * than racing two different pipes for them the way the third-party CDN did.
   *
   * FIRED AT THE REVEAL, NOT AT THE ARM, and that is invariant 5, not a
   * pessimisation. The bundle is padded to one of four ladder rungs, so its
   * `content-length` is four observable values carrying 0.85 bits about the
   * round's TARGET — and `round.arm` arrives ROUND_ARM_LEAD_MS before the
   * rung-1 key makes that target public. Fetching on arm therefore leaked half
   * a second of advance notice about the pinpoint answer through a request the
   * honest client was making anyway. The server refuses the token before T+0
   * regardless (`AssetVault.mint`); this is the half that stops the honest
   * client wasting a request on a 404.
   *
   * Nothing is lost by waiting: pictures live on the TERMINAL rung, so the
   * budget is the whole rung-1 clock and every rung in front of it, not the
   * 500ms arm window.
   *
   * NOT awaited, ever. A slow or failed asset must not delay the reveal — the
   * prompt is the thing the clock is fair about, and `RoundAssets.open` can
   * still fetch late if this missed. Fire and forget is the correct shape.
   */
  const startAssetPreload = (roundIdx: number): void => {
    if (armAssetTokens.roundIdx !== roundIdx) return;
    const tokens = armAssetTokens.tokens;
    if (tokens.length === 0 || !transport) return;
    const t = transport;
    void roundAssets.preload(tokens, (token) => t.fetchAsset(token)).catch(() => undefined);
  };

  /**
   * Turn a freshly-opened rung's `assetIdx` into object URLs.
   *
   * Runs on the SAME key that opened the rung's prompt: one release instant,
   * one secret, so the pictures and the question they belong to become readable
   * together and neither can be read early.
   */
  const resolveRungAssets = (roundIdx: number, rung: RoundPayloadRung, keyB64: string): void => {
    const idx = rungAssetIdx(rung);
    if (idx === undefined) return;
    if (armAssetTokens.roundIdx !== roundIdx) return;
    const token = armAssetTokens.tokens[idx];
    if (token === undefined || !transport) return;
    const t = transport;
    void roundAssets
      .open(token, keyB64, (tok) => t.fetchAsset(tok))
      .then((urls) => {
        if (urls === null) return;
        const live = get().round;
        if (!live || live.roundIdx !== roundIdx) return;
        set({ round: { ...live, assetUrls: { ...live.assetUrls, [rung.rung]: urls } } });
      })
      .catch(() => undefined);
  };

  const scheduleReveal = (roundIdx: number, startAtServerTs: number): void => {
    if (revealTimer !== null) window.clearTimeout(revealTimer);
    const fire = (): void => {
      revealTimer = null;
      const round = get().round;
      if (!round || round.roundIdx !== roundIdx || round.revealed) return;
      // The reducer's `local.reveal` is what opens the round; the store never
      // sets a phase by hand.
      dispatch({
        t: 'local.reveal',
        roundIdx,
        payloadPrompt: round.prompt,
        serverTs: startAtServerTs,
      });
      set({ round: { ...get().round!, revealed: true } });
      // T+0. The target is public from here, so the bundle's length says
      // nothing the player does not already have on screen.
      startAssetPreload(roundIdx);
    };
    const delayMs = startAtServerTs - (transport?.serverNow() ?? Date.now());
    if (delayMs <= 0) {
      fire();
      return;
    }
    revealTimer = window.setTimeout(fire, delayMs);
  };

  /**
   * A key for a rung 2..N that was pre-delivered SEALED inside the round
   * envelope. The gateway releases it the moment the player commits to that
   * rung.
   *
   * The frame now NAMES its rung, so exactly one blob is a candidate and a
   * mis-routed key is a visible failure rather than a silent no-op. Trial
   * decryption survives only as the fallback for a synthetic frame that omits
   * the field; on the wire `parseServerMessage` requires it.
   *
   * Without this path the ladder is unreachable on a live socket: the
   * envelope's own key is consumed by rung 1, and every deeper key would fall
   * on the floor with the rung's prompt left as an unopened `sealed:1:` blob.
   */
  const tryOpenRungKey = async (
    roundIdx: number,
    forRung: number | null,
    key: string,
  ): Promise<boolean> => {
    const round = get().round;
    if (!round || round.roundIdx !== roundIdx) return true; // stale — drop it

    const named = forRung === null ? undefined : round.rungs[forRung];
    const candidates = named !== undefined ? [named] : Object.values(round.rungs);

    for (const rung of candidates) {
      if (!isSealedInline(rung.prompt)) continue;
      const plain = await openInlineJson(rung.prompt, key);
      if (plain === null) continue;
      const opened = parseSealedRung(plain);
      if (!opened) {
        set({ lastError: { code: 'bad_message', message: 'Round payload was unreadable.' } });
        return true;
      }
      const live = get().round;
      if (!live || live.roundIdx !== roundIdx) return true;
      set({ round: { ...live, rungs: mergeRungs(live.rungs, [opened]) } });
      resolveRungAssets(roundIdx, opened, key);
      return true;
    }
    return false;
  };

  /**
   * Drain the deeper-rung keys that have arrived but not yet matched a sealed
   * rung. On a resume the whole burst — arm, rung-1 key, every released rung
   * key — lands in one tick, ahead of the envelope's async decrypt, so a key
   * that opens nothing yet is held rather than dropped.
   */
  const drainRungKeys = (roundIdx: number): void => {
    pendingRungKeys = pendingRungKeys.filter((k) => k.roundIdx === roundIdx);
    for (const entry of [...pendingRungKeys]) {
      void tryOpenRungKey(entry.roundIdx, entry.rung, entry.key)
        .then((consumed) => {
          if (!consumed) return;
          pendingRungKeys = pendingRungKeys.filter((k) => k !== entry);
        })
        .catch(() => {
          set({ lastError: { code: 'bad_message', message: 'Round payload failed to decrypt.' } });
        });
    }
  };

  const onKey = (msg: RoundKeyMessage): void => {
    const rung = msg.rung ?? 1;
    // A key for a deeper rung is a DIFFERENT kind of thing from the round
    // envelope's key, and until the frame carried its rung there was nothing to
    // say so — a deeper key would happily consume an unopened arm and leave the
    // ladder sealed. It may only claim an arm that is itself a deeper-rung
    // delivery (the local driver re-arms per rung); on the socket it always
    // falls through to the inline-blob path below.
    const arm = pendingArms.find(
      (a) => a.roundIdx === msg.roundIdx && !a.opened && (rung <= 1 || a.delivery),
    );
    if (!arm) {
      pendingRungKeys.push({ roundIdx: msg.roundIdx, rung: msg.rung ?? null, key: msg.key });
      drainRungKeys(msg.roundIdx);
      return;
    }
    arm.opened = true;

    void decryptJson(msg.key, arm.iv, arm.ciphertext)
      .then((plain) => {
        const payload = parseRoundPayload(plain);
        if (!payload) {
          set({ lastError: { code: 'bad_message', message: 'Round payload was unreadable.' } });
          return;
        }
        const round = get().round;
        if (!round || round.roundIdx !== payload.roundIdx) return;

        const rungs = mergeRungs(round.rungs, payload.rungs);
        for (const r of payload.rungs) resolveRungAssets(payload.roundIdx, r, msg.key);
        if (arm.delivery) {
          set({ round: { ...round, rungs } });
          drainRungKeys(payload.roundIdx);
          return;
        }
        set({
          round: {
            ...round,
            prompt: payload.prompt,
            ladderDepth: payload.ladderDepth,
            secondsPerRung: payload.secondsPerRung,
            decisionSeconds: payload.decisionSeconds,
            // The arm already set this; the payload has nothing to say about it.
            readSeconds: round.readSeconds,
            orientRegion: payload.orientRegion ?? null,
            pinpointAttempts: payload.pinpointAttempts,
            golden: round.golden || payload.golden === true,
            rungs,
          },
        });
        // Deeper-rung keys that outran the envelope's decrypt can open now.
        drainRungKeys(payload.roundIdx);
        scheduleReveal(payload.roundIdx, arm.startAtServerTs);
      })
      .catch(() => {
        set({ lastError: { code: 'bad_message', message: 'Round payload failed to decrypt.' } });
      });
  };

  // -- inbound --------------------------------------------------------------

  const handle = (msg: ServerMessage): void => {
    switch (msg.t) {
      case 'welcome':
        set({ playerId: msg.playerId, handle: msg.handle });
        writeLocal('handle', msg.handle);
        dispatch(msg);
        return;

      case 'pong':
        if (transport) {
          const s = transport.stats();
          set({ rttMs: s.rttMs, offsetMs: s.offsetMs, clockSynced: s.synced });
        }
        return;

      case 'lobby.state':
        set({ lobby: msg });
        return;

      case 'queued':
        set({
          queue: {
            tier: msg.tier,
            gameMode: msg.gameMode ?? get().gameMode,
            kind: get().queue?.kind ?? 'quick',
            startedAtServerTs: msg.queuedAtServerTs,
            estimatedWaitMs: msg.estimatedWaitMs,
            ...(msg.groupTargetSeats !== undefined
              ? { groupTargetSeats: msg.groupTargetSeats }
              : {}),
            ...(msg.groupFallbackAtServerTs !== undefined
              ? { groupFallbackAtServerTs: msg.groupFallbackAtServerTs }
              : {}),
          },
        });
        return;

      case 'matched': {
        set({ lobby: null });
        setFaultContext({
          matchId: msg.matchId,
          gameMode: msg.gameMode ?? 'country',
          tier: msg.tier,
        });
        breadcrumb(`matched ${msg.kind} ${msg.gameMode ?? 'country'} rounds=${msg.roundCount}`);
        // A resume re-sends `matched` for the match already in progress (spec
        // 8.6). Rebuilding the machine on it would zero the banked total and
        // truncate the history of a match the server is still running, so the
        // same matchId only refreshes the resume token and the opponent card.
        const resuming = machine !== null && get().matchInfo?.matchId === msg.matchId;
        if (resuming) {
          set({
            matchInfo: msg,
            queue: null,
            ...(msg.goldenRoundIdx === undefined ? {} : { goldenRoundIdx: msg.goldenRoundIdx }),
          });
          dispatch(msg);
          startTicker();
          navigate({ name: 'match' });
          return;
        }
        const gameMode = msg.gameMode ?? get().gameMode;
        /*
         * `coached` reaches the machine because the guided round's first round
         * runs on a slower clock, and the machine is what sets the deadline the
         * countdown ring animates to. The practice driver applies the same
         * override through the same `roundClockMs`; if only one of them had it,
         * the ring and the authority would disagree about when the round ends,
         * which reads as the game cheating.
         *
         * Read from the STORE rather than from `msg`: `coached` is a property
         * of how this match was started, it never travels over the wire, and
         * the gateway neither sends nor honours it.
         */
        machine = createMatchMachine(shellPlan(msg.tier, msg.roundCount, gameMode), msg.tier, {
          coached: get().coached,
          /*
           * PACE COMES OFF THE WIRE, unlike `coached` directly above it, and
           * the difference is which side is the authority. Coaching is a local
           * decoration on a local match. Pace decides when the ROOM expires a
           * rung, so the value this machine draws its ring from has to be the
           * room's, not this browser's idea of what it asked for.
           */
          pace: msg.pace ?? 'standard',
        });
        set({
          matchInfo: msg,
          tier: msg.tier,
          gameMode,
          courseId: msg.courseId,
          queue: null,
          history: [],
          matchEnd: null,
          round: null,
          oppPhase: 'idle',
          // CHANGE 6. Derived from the MATCH SEED on the server, so both seats
          // are told the same index; the client only relays it.
          goldenRoundIdx: msg.goldenRoundIdx ?? null,
          match: machine.getState(),
        });
        dispatch(msg);
        startTicker();
        navigate({ name: 'match' });
        return;
      }

      /**
       * Resume snapshot (spec 8.6). Arrives last in the resync burst, so it is
       * the final word on where this seat is: banked total, finished rounds,
       * and the live round's real rung, pot and deadline. The reveal that the
       * re-armed round schedules is a no-op against it — `local.reveal` only
       * moves a round still in `armed`.
       */
      case 'match.sync': {
        set({
          history: [...msg.history],
          ...(msg.goldenRoundIdx === undefined ? {} : { goldenRoundIdx: msg.goldenRoundIdx }),
        });
        dispatch(msg);
        const round = get().round;
        if (round && msg.round && round.roundIdx === msg.round.index) {
          set({
            round: {
              ...round,
              awaitingRung: null,
              golden: round.golden || msg.round.golden === true,
              // A resume must not restore a stale attempt count: the snapshot's
              // is the one the server will charge against.
              attempts:
                msg.round.pinpointAttempt === undefined
                  ? round.attempts
                  : attemptStateFor(round, msg.round.pinpointAttempt),
            },
          });
        }
        return;
      }

      case 'round.arm':
        breadcrumb(
          `arm r${msg.roundIdx}${msg.eclipsed ? ' ECLIPSED' : ''}${
            msg.eclipseRound ? ' eclipse-round' : ''
          }`,
        );
        set({ eclipsed: msg.eclipsed === true, eclipseRound: msg.eclipseRound === true });
        setFaultContext({ roundIdx: msg.roundIdx, rung: 1, phase: 'armed' });
        onArm(msg);
        return;

      case 'round.key':
        // Deliberately NOT forwarded to the reducer. The key's arrival is not
        // the reveal — `scheduleReveal` fires `local.reveal` at this client's
        // own estimate of startAtServerTs, which is the whole point of arming.
        onKey(msg);
        return;

      case 'rung.result': {
        const round = get().round;
        if (round && round.roundIdx === msg.roundIdx) {
          feedbackSeq += 1;
          if (isRungInPlay(msg)) {
            // CHANGE 4 + CHANGE 2. The rung is STILL LIVE. All that is known is
            // that the player's own tap was wrong and what the next attempt is
            // worth. No reveal — the type makes one unrepresentable here — and
            // `targetCca3` stays null, so nothing downstream can flash the
            // answer at a player who is still betting on it.
            set({
              round: {
                ...round,
                lastResult: msg,
                awaitingRung: null,
                attempts: msg.attempts,
                pickFeedback: {
                  seq: feedbackSeq,
                  cca3: msg.pickedCca3,
                  correct: false,
                  retryable: true,
                },
              },
            });
          } else {
            const reveal = msg.reveal ?? null;
            set({
              round: {
                ...round,
                lastResult: msg,
                awaitingRung: null,
                ...(msg.attempts ? { attempts: msg.attempts } : {}),
                ...(msg.numericBand ? { numericBand: msg.numericBand } : {}),
                reveal,
                golden: round.golden || msg.golden === true,
                // CHANGE 2, the green half. On a resolved PINPOINT the reveal
                // carries the player's own pick back, so the globe can confirm
                // it without the store having to remember what it sent.
                pickFeedback:
                  reveal?.kind === 'pinpoint'
                    ? {
                        seq: feedbackSeq,
                        cca3: reveal.pickedCca3,
                        correct: msg.correct,
                        retryable: false,
                      }
                    : round.pickFeedback,
                // Only the PINPOINT reveal names the country; a probe reveal
                // names an option index, and by then the target is already known.
                targetCca3: reveal?.kind === 'pinpoint' ? reveal.correctCca3 : round.targetCca3,
                targetLngLat:
                  reveal?.kind === 'pinpoint' ? reveal.correctLngLat : round.targetLngLat,
                targetLabel:
                  reveal?.kind === 'pinpoint'
                    ? (reveal.correctLabel ?? null)
                    : round.targetLabel,
              },
            });
          }
        }
        dispatch(msg);
        return;
      }

      case 'opp.state':
        dispatch(msg);
        set({ oppPhase: msg.phase, oppSeq: get().oppSeq + 1 });
        return;

      case 'round.end': {
        const round = get().round;
        if (round && round.roundIdx === msg.roundIdx) {
          set({
            round: {
              ...round,
              targetCca3: msg.you.targetCca3,
              targetLngLat: msg.targetLngLat,
              targetLabel: msg.targetLabel ?? round.targetLabel,
              awaitingRung: null,
            },
          });
        }
        set({ history: [...get().history, msg] });
        dispatch(msg);
        return;
      }

      case 'match.end':
        dispatch(msg);
        set({ matchEnd: msg });
        if (get().mode === 'daily') {
          // Find OUR seat. `scores[0]` happens to be the human today, because a
          // daily never pairs live, but recording the opponent's total as your
          // daily result is not a bug worth leaving one refactor away.
          const me = get().playerId;
          const mine =
            msg.scores.find((s) => s.playerId === me) ?? (me === null ? msg.scores[0] : undefined);
          writeLocal(dailyKey(todayKey(), get().tier, get().gameMode), String(mine?.total ?? 0));
        }
        // Let the final round's reveal breathe before the results screen — but
        // KEEP the handle. `teardown` (and therefore `startMatch`) cancels it,
        // so "Play again" inside the beat does not get yanked out of the queue.
        if (matchEndTimer !== null) window.clearTimeout(matchEndTimer);
        matchEndTimer = window.setTimeout(() => {
          matchEndTimer = null;
          stopTicker();
          navigate({ name: 'results' });
        }, 1_400);
        return;

      case 'error':
        set({ lastError: { code: msg.code, message: msg.message } });
        if (msg.fatal) {
          dispatch(msg);
          teardown();
        }
        return;

      default:
        return;
    }
  };

  // -- the input gate (spec 8.2) -------------------------------------------

  /**
   * HARD GATE. Any input event that arrives before the round's reveal instant
   * is DISCARDED — never buffered, never replayed a moment later. Queuing it
   * would hand a patched client the 500ms of arm lead that encrypting the
   * payload exists to deny it.
   */
  const inputArmed = (): boolean => {
    const s = get();
    if (!transport || !machine) return false;
    const round = s.round;
    if (!round || !round.revealed) return false;
    if (transport.serverNow() < round.startAtServerTs) return false;
    const m = s.match;
    if (!m || m.finished) return false;
    if (m.round === null || m.round.index !== round.roundIdx) return false;
    return true;
  };

  const send = (msg: Parameters<GameTransport['send']>[0]): void => {
    transport?.send(msg);
  };

  /**
   * Hand the reducer the clock for a push, one credited round trip ahead.
   *
   * `local.push` sets the deeper rung's deadline to `now + secondsPerRung`, and
   * this client cannot READ that rung until its key has made the full round
   * trip (push out, key back). Without the credit a 400ms player gets 9600ms of
   * a 10000ms window and is cut off by their OWN reducer — `submitAnswer`
   * requires `phase === 'probe'` — before the server ever sees the answer.
   *
   * The server applies the identical credit from the push's arrival instant
   * (`MatchRoom.pushTickServerTs`), which is one hop later, so its deadline
   * lands one hop after this one: exactly the slack the answer needs to fly
   * home. Same cap on both sides (invariant 6) — if they capped differently one
   * side would be accepting input the other had already closed.
   *
   * The cap is `WINDOW_RTT_CREDIT_CAP_MS`, NOT `CREDITED_RTT_CAP_MS`. Those were
   * the same number and the sharing was a bug: the points cap is deliberately
   * tight because a stalled pong buys speed bonus, but applying it here left an
   * 800ms link with 9600ms of a 10000ms rung and a `round_closed` rejection for
   * a perfectly legal answer. See the comment on both constants.
   *
   * Credit is only taken from a REAL sample: an unsynced clock reports rtt 0.
   */
  const pushTickServerTs = (): number => {
    const s = get();
    const now = transport?.serverNow() ?? Date.now();
    if (!s.clockSynced) return now;
    const credit = Math.round(windowCreditMs(s.rttMs));
    if (credit <= 0) return now;
    const deadline = s.match?.round?.deadlineServerTs ?? null;
    if (deadline === null || now >= deadline) return now;
    // Never let the credit fire the decision deadline it stands in front of.
    return Math.min(now + credit, deadline - 1);
  };

  // -- store ----------------------------------------------------------------

  return {
    status: 'idle',
    playerId: null,
    handle: persistedHandle(),
    rttMs: 0,
    offsetMs: 0,
    clockSynced: false,
    /*
     * NULL ON BOOT, ALWAYS, even for a browser that signed in yesterday. The
     * identity token in localStorage is what makes this device that player,
     * account or no account; asking the gateway to confirm one it holds no
     * session for would be a round trip that changes nothing on screen.
     */
    /*
     * Read from storage rather than started at null.
     *
     * There is still no "am I signed in?" call on boot and there does not need
     * to be: the identity token is what makes this browser a particular
     * player, and `rememberedAccount` only answers when the remembered address
     * belongs to the player that token names.
     */
    account: rememberedAccount(),

    mode: null,
    lobby: null,
    eclipsed: false,
    eclipseRound: false,
    inviteToken: null,
    gameMode: 'country',
    /**
     * A first-time visitor lands on EXPLORER, a returning one on whatever they
     * last played. Preselecting the middle tier for somebody who has never seen
     * the game costs them the three-attempt pinpoint they most need.
     */
    tier: initialTier(),
    courseId: null,
    queue: null,
    matchInfo: null,
    match: null,
    round: null,
    oppPhase: 'idle',
    oppSeq: 0,
    goldenRoundIdx: null,
    coached: false,
    history: [],
    matchEnd: null,
    lastError: null,

    startMatch: (opts) => {
      teardown();
      markPlayed();
      persistTier(opts.tier);
      const gameMode = opts.gameMode ?? 'country';
      const kind: MatchKind =
        opts.mode === 'daily'
          ? 'daily'
          : opts.mode === 'friend'
            ? 'friend'
            : opts.mode === 'group'
              ? 'group'
              : 'quick';

      set({
        mode: opts.mode,
        inviteToken: opts.shareToken ?? null,
        gameMode,
        tier: opts.tier,
        courseId: opts.courseId ?? null,
        queue: {
          tier: opts.tier,
          gameMode,
          kind,
          startedAtServerTs: Date.now(),
          estimatedWaitMs: 6_000,
        },
        matchInfo: null,
        match: null,
        round: null,
        history: [],
        matchEnd: null,
        lastError: null,
        oppPhase: 'idle',
        goldenRoundIdx: null,
        coached: opts.coached === true,
        /*
         * Starting ANY match means this browser is no longer standing in
         * whatever lobby it was in. Cleared here as well as in `leaveMatch`
         * because this is the path that cannot be skipped: a stale lobby on
         * screen while a quick match queues behind it is the whole bug, and
         * every route into a match comes through here.
         *
         * Safe for a friend match too. That opens a lobby of its own and the
         * gateway sends `lobby.state` for it a moment later.
         */
        lobby: null,
      });

      /** Wire a transport into this store and open it. */
      const attach = (t: GameTransport): void => {
        transport = t;
        unsubscribe.push(t.onMessage(handle));
        let queued = false;
        unsubscribe.push(
          t.onStatus((status) => {
            set({ status });
            if (status !== 'open' || !transport) return;
            const s = transport.stats();
            set({ rttMs: s.rttMs, offsetMs: s.offsetMs, clockSynced: s.synced });
            // Queue exactly once. A reconnect must NOT re-queue: `hello` carries
            // the resume token and the gateway puts us back in the seat we left
            // (spec 8.6). Re-queueing would abandon a match still in progress.
            if (queued) return;
            queued = true;
            send({
              t: 'queue',
              tier: opts.tier,
              gameMode,
              kind,
              ...(opts.courseId ? { courseId: opts.courseId } : {}),
              ...(opts.shareToken ? { shareToken: opts.shareToken } : {}),
            });
          }),
        );
        t.connect();
      };

      if (opts.transport) {
        attach(opts.transport);
      } else if (opts.mode === 'practice') {
        /**
         * INVARIANT 5 — THE PRACTICE DRIVER IS LOADED ON DEMAND AND NEVER IN A
         * RANKED MATCH.
         *
         * `net/local.ts` is a full gateway running in this process, so it
         * imports `@geogame/rules`, which imports `@geogame/data/facts`: every
         * capital, population, border list and UN accession year in the game.
         * Statically imported, all of that landed in the ENTRY chunk that every
         * ranked player downloads, and one regex over the shipped JS recovered
         * 198 complete country records — `unJoinedYear` included, which is the
         * answer key for the golden rung paying x9.
         *
         * Behind a dynamic import it lives in a chunk that is fetched only when
         * somebody starts a practice match. Practice genuinely holds the key —
         * it IS the server — and it is unranked; what matters is that a ranked
         * client never loads it. Exactly the precedent `net/localassets.ts`
         * set for the flag artwork map, for exactly the same reason, and
         * `scripts/e2e.ts` asserts it against the built bundle.
         */
        const token = (startToken += 1);
        void import('../net/local').then(({ LocalDriver }) => {
          // A second `startMatch`, or a `leaveMatch`, may have happened while
          // the chunk was in flight. Attaching then would wire a driver into a
          // store that has already torn down.
          if (token !== startToken) return;
          attach(
            new LocalDriver({
              tier: opts.tier,
              gameMode,
              kind,
              // Omitted rather than blanked when the player has not named
              // themselves: the driver mints the same anonymous handle the
              // gateway would, so practice and a live match agree.
              ...(normalizeHandle(get().handle) !== null
                ? { handle: normalizeHandle(get().handle) as string }
                : {}),
              // The slow first-round clock. Only the driver and the machine
              // read this; it is not a wire concept.
              ...(opts.coached === true ? { coached: true } : {}),
              // Offline, so this driver is the whole authority on the clock.
              ...(opts.pace !== undefined ? { pace: opts.pace } : {}),
              ...(opts.courseId ? { courseId: opts.courseId } : {}),
              ...(opts.seed ? { seed: opts.seed } : {}),
            }),
          );
        });
      } else {
        attach(
          new SocketTransport({
            // Read through `normalizeHandle` rather than sent raw: the store
            // holds exactly what is in the text box, spaces and all, because
            // `setHandle` is forbidden from reshaping it mid-keystroke.
            ...(normalizeHandle(get().handle) !== null
              ? { handle: normalizeHandle(get().handle) as string }
              : {}),
            getResumeToken: () => helloToken(),
            setIdentityToken: (t) => writeLocal('identityToken', t),
            setMatchToken: (t) => writeLocal('matchToken', t),
          }),
        );
      }

      navigate({ name: 'queue' });
    },

    startLobby: () => {
      send({ t: 'lobby.start' });
    },

    /*
     * The store's own handle is set FIRST and unconditionally, so the name
     * survives whether or not a socket is listening: it is what the next
     * `hello` will assert, and `onHello` lets a supplied handle beat the one
     * baked into a resume token. The frame is the live half -- it is what puts
     * the new name on everybody else's roster before the match starts.
     */
    renameInLobby: (handle) => {
      get().setHandle(handle);
      const trimmed = normalizeHandle(handle);
      if (trimmed !== null) send({ t: 'rename', handle: trimmed });
    },

    configureLobby: (next) => {
      send({ t: 'lobby.configure', ...next });
    },

    promoteInLobby: (playerId) => {
      send({ t: 'lobby.promote', playerId });
    },

    signOut: () => {
      forgetAccount();
      clearLocal('identityToken');
      clearLocal('matchToken');
      clearLocal('handle');
      set({ account: null, handle: '' });
    },

    cancelQueue: () => {
      send({ t: 'dequeue' });
      teardown();
      set({ queue: null, mode: null, status: 'idle' });
      navigate({ name: 'home' });
    },

    leaveMatch: () => {
      teardown();
      set({
        /*
         * `lobby` BELONGS IN THIS LIST, and leaving it out was a bug that took
         * you into a match you never asked for.
         *
         * `QueueScreen` draws the lobby whenever `lobby` is non-null. Leaving
         * a lobby cleared everything except that, so the next quick match
         * queued underneath a lobby screen that had no right to still be on
         * display -- and eight seconds later the matchmaker paired the player
         * and dropped them into a game they had not started. From their side
         * the lobby simply exploded into a match.
         *
         * Nothing else clears it either: only `matched` did, which is the one
         * moment it was already too late to matter.
         */
        lobby: null,
        queue: null,
        mode: null,
        match: null,
        round: null,
        matchInfo: null,
        status: 'idle',
      });
      navigate({ name: 'home' });
    },

    submitPinpoint: (lngLat, cca3) => {
      if (!inputArmed()) return false;
      const s = get();
      if (s.match?.phase !== 'pinpoint') return false;
      if (s.match.round?.pinLngLat) return false;
      // CHANGE 4. Never spend an attempt the tier has not got. The server is
      // authoritative and answers `no_attempts_left`; this only stops the
      // client asking, and stops the optimistic reducer counting an attempt
      // that will be refused.
      const spent = s.round?.attempts?.attempt ?? 0;
      if (spent >= (s.round?.pinpointAttempts.maxAttempts ?? 1)) return false;
      // spec 8.3 — the RAW client clock. The server subtracts the offset itself
      // and clamps to the physically-possible floor, so a converted value here
      // would be double-corrected.
      const clientTs = Date.now();
      dispatch({ t: 'local.pinpoint', roundIdx: s.round!.roundIdx, lngLat, cca3, clientTs });
      set({ round: { ...s.round!, awaitingRung: 1 } });
      send({ t: 'pinpoint', roundIdx: s.round!.roundIdx, entityCode: cca3, lngLat, clientTs });
      return true;
    },

    submitAnswer: (optionIdx) => {
      if (!inputArmed()) return false;
      const s = get();
      const round = s.round;
      const rung = s.match?.round?.rung ?? 0;
      if (!round || s.match?.phase !== 'probe') return false;
      if (round.awaitingRung !== null) return false;
      if (!round.rungs[rung]) return false;
      const clientTs = Date.now();
      const answer = indexAnswer(optionIdx);
      dispatch({ t: 'local.answer', roundIdx: round.roundIdx, rung, answer, clientTs });
      set({ round: { ...round, awaitingRung: rung } });
      send({ t: 'answer', roundIdx: round.roundIdx, rung, answer, clientTs });
      return true;
    },

    /**
     * CHANGE 6. Identical to `submitAnswer` but for an open numeric rung: same
     * gate, same reducer event, same frame — only the `AnswerValue` variant
     * differs. Two entry points rather than one polymorphic one because every
     * existing caller passes an option index and a union at that boundary would
     * make each of them prove which kind it meant.
     */
    submitNumeric: (value) => {
      if (!inputArmed()) return false;
      const s = get();
      const round = s.round;
      const rung = s.match?.round?.rung ?? 0;
      if (!round || s.match?.phase !== 'probe') return false;
      if (round.awaitingRung !== null) return false;
      const payload = round.rungs[rung];
      // A numeric answer only means anything on a numeric rung; sending one
      // anywhere else is a client bug, not a submission.
      if (!payload || payload.kind !== 'numeric') return false;
      if (!Number.isFinite(value)) return false;
      const clientTs = Date.now();
      const answer = numericAnswer(Math.trunc(value));
      dispatch({ t: 'local.answer', roundIdx: round.roundIdx, rung, answer, clientTs });
      set({ round: { ...round, awaitingRung: rung } });
      send({ t: 'answer', roundIdx: round.roundIdx, rung, answer, clientTs });
      return true;
    },

    bank: () => {
      if (!inputArmed()) return false;
      const s = get();
      if (!machine || !s.round) return false;
      if (!machine.selectors.canBank(s.match!)) return false;
      const clientTs = Date.now();
      send({ t: 'bank', roundIdx: s.round.roundIdx, clientTs });
      dispatch({ t: 'local.bank', roundIdx: s.round.roundIdx, clientTs });
      return true;
    },

    push: () => {
      if (!inputArmed()) return false;
      const s = get();
      if (!machine || !s.round) return false;
      if (!machine.selectors.canPush(s.match!)) return false;
      const clientTs = Date.now();
      send({ t: 'push', roundIdx: s.round.roundIdx, clientTs });
      dispatch({ t: 'local.tick', serverTs: pushTickServerTs() });
      dispatch({ t: 'local.push', roundIdx: s.round.roundIdx, clientTs });
      return true;
    },

    serverNow: () => transport?.serverNow() ?? Date.now(),

    /*
     * THIS RUNS ON EVERY KEYSTROKE, so it may not reject or reshape what it is
     * given — the input is controlled, and anything this refuses to store is
     * a character the player watches reappear under their thumb.
     *
     * Two bugs lived in the three lines this replaces. `if (!trimmed) return`
     * made the LAST REMAINING CHARACTER undeletable: backspacing to '' was
     * rejected, the store kept the single letter, and the field snapped back
     * to it. Desktop hid it, because select-all-then-type never passes through
     * the empty state; on a phone, where you delete one character at a time,
     * every name was stuck starting with the same letter it started with.
     * `.trim()` was the second: it ate the trailing space of "Tanner " before
     * a surname could be typed after it.
     *
     * Trimming belongs at the point of USE, not the point of entry — see
     * `normalizeHandle`, which every send site runs.
     */
    adoptIdentity: (next) => {
      /*
       * Written FIRST, and into the IDENTITY slot: this token is minted with
       * kind `id`, so it is the thirty-day proof of who this browser is and
       * the one REST will accept. It must never go to the match slot, which
       * holds a one-hour token and is cleared the moment a match ends.
       * Everything else here is this tab catching up with it.
       */
      writeLocal('identityToken', next.token);
      writeLocal('handle', next.handle);
      // Remembered against THIS player id, so a later anonymous identity
      // cannot inherit the claim. See `rememberAccount`.
      rememberAccount(next.email, next.playerId);
      set({
        playerId: next.playerId,
        handle: next.handle,
        account: { email: next.email },
      });
    },

    setHandle: (h) => {
      const next = h.slice(0, HANDLE_MAX_LENGTH);
      writeLocal('handle', next);
      set({ handle: next });
    },

    dismissError: () => set({ lastError: null }),
  };
});
